/**
 * ATL-886 — resolve the organization's read-cutover flag BEFORE a data source is chosen.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS TO FIX, because it is not obvious from the call site
 *
 * `shouldUseSupabase()` decides which store the app reads from. Its per-org clause is
 * `getOrganization()?.useSupabaseForData === true`, and `getOrganization()` reads
 * `organizationAtom` — declared `atom({})` in `AtomsProvider.ts`, i.e. a plain IN-MEMORY atom that
 * is NOT persisted and is therefore `{}` on every cold start.
 *
 * The organization document that carries the flag was only ever loaded inside
 * `AppStack.getInitialData`'s `if (!useSupabase)` Firestore branch — that is, AFTER the branch the
 * flag was supposed to select. So on every cold start the flag read `undefined`, the Firestore
 * branch was taken, and the org document then arrived carrying `useSupabaseForData: true` far too
 * late to matter. `getInitialData` is fired from a `useEffect` keyed on `[db, user]` with no
 * `organization` dependency, so it never re-ran either.
 *
 * **The flag that selects the data source was being read out of data that only the branch it was
 * supposed to select would load.** The per-org read cutover could therefore never take effect, for
 * any organization, ever. Proven in production on 2026-08-07: ELEGANCE
 * (`AKKU6Ort8237IttikmPO`) carried `useSupabaseForData: true` in BOTH stores and the app still
 * logged `📊 Loading data from Firestore`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS READS FIRESTORE, IN BOTH READ MODES
 *
 * The organization document lives in Firestore whichever store the app is about to read from — the
 * Supabase path mirrors it, it does not replace it — so this one read is available before the
 * branch regardless of which way the branch will go. It costs a single document read at startup.
 *
 * WHY NOT CACHE THE FLAG IN AsyncStorage, the way the DEVICE override does
 *
 * `getDeviceSupabaseOverride()` legitimately primes a synchronous cache from AsyncStorage at module
 * load, because a per-DEVICE switch is a property of the device and cannot be wrong for the session
 * it is about to start. The per-ORG flag is different in two ways that make a cache unsafe:
 *
 *   1. It is per organization, so a cached boolean would be wrong the moment a device signs into a
 *      different org — and it would be wrong in the direction that silently moves a merchant's
 *      reads onto a store nobody cleared for them.
 *   2. It is the operator's kill switch. A cached value means a rollback does not take effect on
 *      the launch that follows it, which is the launch that matters.
 *
 * Reading it fresh costs one round trip and cannot be stale. That is the right trade for a switch
 * that decides which database a shop's till reads from.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * FAILURE IS ALWAYS RESOLVED TOWARDS FIRESTORE, NEVER TOWARDS SUPABASE
 *
 * If the document is missing, unreadable, or the read throws, this leaves `organizationAtom`
 * untouched so `shouldUseSupabase()` falls back to its default of Firestore. **An unreadable flag is
 * not consent to move a merchant onto a different database.** It never throws, because a startup
 * path that can reject would take the whole app down over a flag lookup.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE SAME READ ALSO REPORTS THE WRITE FLAG (ATL-1445 stage 3, P4)
 *
 * `writesMaster` is read from the SAME document read, so the two planes of one session are decided
 * from one observation (stage-3 §4: "Extend `primeOrganizationForReadCutover` to resolve
 * `writesMaster`"). This function only REPORTS what the document said and where it came from
 * (`writesMaster` in the result); it decides nothing about writes. The rules that turn the
 * observation into a write mode — and why they run the opposite way from the read flag's (an
 * unreadable write flag keeps a remembered `'supabase'`) — are in `global/Write/writeMode.ts`.
 * The read-flag behaviour above is unchanged by it.
 */

import { snapshotExists } from './types'
import { writesMasterOfField, type WritesMasterObservation } from '../Write/writeMode'

/** The subset of a Firestore DocumentSnapshot this module relies on. */
export interface OrganizationDocSnapshot {
  /** Boolean property on web (firebase/compat), method on native (v22) — read via `snapshotExists`. */
  exists?: unknown
  id?: string
  ref?: unknown
  data?: () => Record<string, unknown> | undefined
  /**
   * `SnapshotMetadata` on both SDKs. `fromCache: false` is the only proof the document came from the
   * server; anything else (true, absent, not a boolean) is treated as a cached read.
   */
  metadata?: { fromCache?: unknown }
}

/**
 * Where a snapshot came from. Only an explicit `fromCache === false` is a server read: a missing or
 * malformed metadata object is a cache read, because a cached write flag may raise but never lower
 * a remembered `'supabase'` (`writeMode.ts`), and "unknown" must land on the side that cannot lower.
 */
const provenanceOf = (snapshot: OrganizationDocSnapshot): 'server' | 'cache' =>
  snapshot.metadata?.fromCache === false ? 'server' : 'cache'

/** The write flag an existing document's payload carries (absent ⇒ `'firestore'`, invalid ⇒ unreadable). */
const observeWritesMaster = (
  organization: Record<string, unknown>,
  provenance: 'server' | 'cache',
): WritesMasterObservation => {
  const value = writesMasterOfField(organization.writesMaster)
  return value ? { kind: 'read', provenance, value } : { kind: 'unreadable', why: 'invalid-value' }
}

/** What the resolution did, so the caller can log precisely and tests can assert it. */
export type OrgReadCutoverStatus =
  /** The document was read and pushed into the atom. `cutover` is the flag it carried. */
  | 'resolved'
  /** No organization id was available — nothing to read. */
  | 'no-org-id'
  /** The document does not exist. */
  | 'absent'
  /** The document exists but carries no readable payload. */
  | 'empty'
  /** The read threw. */
  | 'error'

export interface OrgReadCutoverResult {
  status: OrgReadCutoverStatus
  /**
   * Whether the resolved document asks for Supabase reads. **Always `false` unless
   * `status === 'resolved'`** — an unresolved flag defaults to Firestore.
   */
  cutover: boolean
  /**
   * ATL-1445 P4: what the same document read said about the WRITE flag — reported, not decided
   * (`global/Write/writeMode.ts` decides). An absent document is a read of `'firestore'` (every
   * organization before its cutover; the setter refuses to flip one without a document); a failed
   * read, an empty payload or a value outside `'firestore'`/`'supabase'` is unreadable.
   */
  writesMaster: WritesMasterObservation
}

export interface PrimeOrganizationParams {
  organizationId: string | null | undefined
  /** Fetches the org document. Injected so this is testable without a Firestore instance. */
  fetchOrganizationDoc: (organizationId: string) => Promise<OrganizationDocSnapshot | null>
  /** Pushes the resolved document into `organizationAtom`. */
  setOrganization: (organization: Record<string, unknown>) => void
  logger?: Pick<Console, 'warn'>
}

/**
 * Load the organization document and push it into `organizationAtom` so that a subsequent
 * `shouldUseSupabase()` can see the per-org read-cutover flag.
 *
 * MUST be awaited before the data-source branch is chosen. That ordering is the entire fix, and it
 * is asserted by `orgReadCutoverOrdering.test.js` because it cannot be enforced by types.
 *
 * @returns what happened, for logging and for the caller's regression canary. Never throws.
 */
export const primeOrganizationForReadCutover = async ({
  organizationId,
  fetchOrganizationDoc,
  setOrganization,
  logger = console,
}: PrimeOrganizationParams): Promise<OrgReadCutoverResult> => {
  if (!organizationId) {
    logger.warn(
      '[read-cutover] no organizationId available; reads default to Firestore'
    )
    return { status: 'no-org-id', cutover: false, writesMaster: { kind: 'unreadable', why: 'no-org-id' } }
  }

  try {
    const snapshot = await fetchOrganizationDoc(organizationId)

    // The explicit null test is not redundant with `snapshotExists` (which handles null itself):
    // it is what narrows `snapshot` for the property reads below, without a non-null assertion.
    if (!snapshot || !snapshotExists(snapshot)) {
      logger.warn(
        '[read-cutover] organization document not found; reads default to Firestore',
        { organizationId }
      )
      return {
        status: 'absent',
        cutover: false,
        // A real snapshot of a missing document is a read of 'firestore' (no document ⇒ never
        // flipped). A null "snapshot" is not something Firestore returns: nothing was observed.
        writesMaster: snapshot
          ? { kind: 'read', provenance: provenanceOf(snapshot), value: 'firestore' }
          : { kind: 'unreadable', why: 'read-error' },
      }
    }

    // `data()` may still be absent on a malformed stub, so it is called defensively.
    const organization = snapshot.data?.()
    if (!organization) {
      logger.warn(
        '[read-cutover] organization document carries no data; reads default to Firestore',
        { organizationId }
      )
      return { status: 'empty', cutover: false, writesMaster: { kind: 'unreadable', why: 'empty-document' } }
    }

    const writesMaster = observeWritesMaster(organization, provenanceOf(snapshot))

    // Mirror exactly what the Firestore org listener attaches, so that priming the atom here and
    // the listener overwriting it later are indistinguishable to every consumer. `ref` in
    // particular is load-bearing: `getFirestoreRef()` converts it, and the billing screens call
    // `.ref.update()` through it (ATL-851).
    organization.id = snapshot.id
    organization.ref = snapshot.ref
    setOrganization(organization)

    return { status: 'resolved', cutover: organization.useSupabaseForData === true, writesMaster }
  } catch (error) {
    // Deliberately swallowed. This runs on the startup path, and a rejection here would take the
    // app down over a flag lookup whose safe answer is already known: read Firestore.
    logger.warn(
      '[read-cutover] could not resolve the organization read-cutover flag; reads default to Firestore',
      { organizationId, error }
    )
    return { status: 'error', cutover: false, writesMaster: { kind: 'unreadable', why: 'read-error' } }
  }
}
